import { type GlobalSearchResults, displayPin } from '#shared/types';
import type { Permission } from '#shared/types';
import { routes } from '#client/lib/routes';
import { COMMANDS, matchCommand } from './commands';

/** Which icon a row carries; the component maps it to the glyph. */
export type PaletteRowKind = 'patient' | 'report' | 'lab-test' | 'recent' | 'command';

export interface PaletteRow {
  key: string;
  group: string;
  label: string;
  detail?: string | undefined;
  href: string;
  kind: PaletteRowKind;
}

/** Two characters minimum: one matches most of the database and the result is noise. */
const MIN_QUERY_LENGTH = 2;

export function isSearchable(query: string): boolean {
  return query.length >= MIN_QUERY_LENGTH;
}

interface RowSources {
  /** The search hits for `query`, or undefined when there are none to show. */
  results: GlobalSearchResults | undefined;
  recent: readonly { href: string; title: string; subtitle?: string }[];
  /** The trimmed query. */
  query: string;
  can: (capability: Permission) => boolean;
}

function searchRows(results: GlobalSearchResults | undefined, canViewCatalogue: boolean) {
  const patients = (results?.patients ?? []).map((patient): PaletteRow => ({
    key: `patient-${patient.id}`,
    group: 'Patients',
    label: patient.name,
    detail: `${displayPin(patient.pin_no)} · ${patient.phone_number}`,
    href: routes.patient(patient.id),
    kind: 'patient',
  }));
  const reports = (results?.reports ?? []).map((report): PaletteRow => ({
    key: `report-${report.id}`,
    group: 'Reports',
    label: report.name,
    detail: `${report.patient.name} · ${displayPin(report.patient.pin_no)}`,
    href: routes.report(report.id),
    kind: 'report',
  }));
  const labTests = canViewCatalogue
    ? (results?.lab_tests ?? []).map((test): PaletteRow => ({
        key: `test-${test.id}`,
        group: 'Lab tests',
        label: test.name,
        detail: test.code,
        href: routes.labTest(test.id),
        kind: 'lab-test',
      }))
    : [];
  return [...patients, ...reports, ...labTests];
}

/**
 * Every row the palette lists, in display order: search hits, then recents,
 * then the commands the user may run that match the query.
 */
export function buildPaletteRows({ results, recent, query, can }: RowSources): PaletteRow[] {
  // Recents only when there is nothing to search for — they are the answer to
  // "take me back", not a competitor to an active query.
  const recentRows = query
    ? []
    : recent.map((item): PaletteRow => ({
        key: `recent-${item.href}`,
        group: 'Recent',
        label: item.title,
        detail: item.subtitle,
        href: item.href,
        kind: 'recent',
      }));

  const commandRows = COMMANDS.filter(
    (command) => (!command.capability || can(command.capability)) && matchCommand(command, query),
  ).map((command): PaletteRow => ({
    key: command.id,
    group: command.group,
    label: command.label,
    href: command.href,
    kind: 'command',
  }));

  return [...searchRows(results, can('labTest.view')), ...recentRows, ...commandRows];
}

/** Each row, flagged when it is the first of its group and so carries the heading. */
export function withGroupHeadings(
  rows: readonly PaletteRow[],
): { row: PaletteRow; startsGroup: boolean }[] {
  return rows.map((row, index) => ({
    row,
    startsGroup: row.group !== (index === 0 ? '' : rows[index - 1]?.group),
  }));
}

const NAVIGATION_KEYS = new Set(['ArrowDown', 'ArrowUp', 'Home', 'End']);

/** Keys that move the highlight. Enter is not one — it opens the row. */
export function isNavigationKey(key: string): boolean {
  return NAVIGATION_KEYS.has(key);
}

/** Where a navigation key moves the highlight, or null when the key does not navigate. */
export function nextActiveIndex(key: string, current: number, count: number): number | null {
  switch (key) {
    case 'ArrowDown':
      return Math.min(current + 1, count - 1);
    case 'ArrowUp':
      return Math.max(current - 1, 0);
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

/** Clamp rather than reset: the highlight should survive results narrowing. */
export function clampActiveIndex(active: number, count: number): number {
  return Math.min(active, Math.max(count - 1, 0));
}

export function emptyMessage(query: string): string {
  return query.length === 1
    ? 'Keep typing — at least two characters.'
    : `Nothing matches “${query}”.`;
}
